// src/pages/Pricing.js
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { ENDPOINTS } from "../config/api";
// eslint-disable-next-line no-unused-vars
import { SUBSCRIPTION_PLANS } from "../data/mockData";
import { Check, X, Zap, Crown, Sparkles, Loader2 } from "lucide-react";

const PLAN_ICONS = {
  free: <Zap size={20} className="text-gray-400" />,
  advanced: <Sparkles size={20} className="text-violet-400" />,
  premium: <Crown size={20} className="text-amber-400" />,
};

const ACCENT = {
  gray: {
    ring: "ring-gray-400",
    badge: "bg-gray-600 text-gray-100",
    btn: "bg-gray-600 hover:bg-gray-500 text-white",
    header: "from-gray-100 to-gray-200",
  },
  violet: {
    ring: "ring-violet-400",
    badge: "bg-violet-100 text-violet-800 border border-violet-300",
    btn: "bg-violet-600 hover:bg-violet-500 text-white",
    header: "from-violet-50 to-indigo-50",
  },
  amber: {
    ring: "ring-amber-400",
    badge: "bg-amber-100 text-amber-800 border border-amber-300",
    btn: "bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white",
    header: "from-amber-50 to-orange-50",
  },
};

function PricingCard({ plan, isCurrent, onSelect, processing }) {
  const a = ACCENT[plan.accentColor] || ACCENT.gray;
  const isPopular = plan.popular || false;

  return (
    <div
      className={`card flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-card-hover ${
        isCurrent ? `ring-2 ${a.ring}` : ""
      } ${isPopular ? "relative" : ""}`}>
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-violet-600 text-white text-[11px] font-bold px-3 py-1 rounded-full shadow-glow-violet whitespace-nowrap">
            Most Popular
          </span>
        </div>
      )}

      {/* Header */}
      <div
        className={`bg-gradient-to-br ${a.header} px-6 pt-6 pb-5 rounded-t-2xl border-b border-gray-200`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-white border border-gray-200 flex items-center justify-center">
              {PLAN_ICONS[plan.id]}
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">
                {plan.name}
              </h3>
              {isCurrent && (
                <span
                  className={`text-[10px] font-semibold ${a.badge} px-1.5 py-0.5 rounded-md`}>
                  Current Plan
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-baseline gap-1">
          {plan.price === 0 ? (
            <span className="text-3xl font-black text-gray-900">
              Free
            </span>
          ) : (
            <>
              <span className="text-sm font-semibold text-gray-500">
                ₦
              </span>
              <span className="text-3xl font-black text-gray-900">
                {plan.price.toLocaleString()}
              </span>
              <span className="text-sm text-gray-500">{plan.period}</span>
            </>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1.5">{plan.description}</p>
      </div>

      {/* Features */}
      <div className="flex-1 px-6 py-5">
        <ul className="space-y-3">
          {plan.features.map((f, i) => (
            <li key={i} className="flex items-start gap-2.5">
              {f.included ? (
                <Check
                  size={15}
                  className="text-emerald-400 flex-shrink-0 mt-0.5"
                />
              ) : (
                <X
                  size={15}
                  className="text-gray-400 dark:text-gray-700 flex-shrink-0 mt-0.5"
                />
              )}
              <span
                className={`text-sm ${f.included ? "text-gray-700 dark:text-gray-300" : "text-gray-400 dark:text-gray-600"}`}>
                {f.text}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* CTA */}
      <div className="px-6 pb-6">
        <button
          onClick={() => !isCurrent && onSelect && onSelect()}
          disabled={isCurrent || processing}
          className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 active:scale-95 flex items-center justify-center gap-2 ${
            isCurrent
              ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-default"
              : processing
              ? "bg-gray-400 cursor-not-allowed"
              : `${a.btn}`
          }`}>
          {processing ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Processing...
            </>
          ) : isCurrent ? (
            "Current Plan"
          ) : plan.price === 0 ? (
            "Get Started Free"
          ) : (
            `Choose ${plan.name}`
          )}
        </button>
      </div>
    </div>
  );
}

export default function Pricing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Fetch available plans from API
  useEffect(() => {
    const fetchPlans = async () => {
      // Don't fetch if token is missing
      if (!user?.token) {
        console.log("Skipping plan fetch: no user token");
        console.log("Using fallback mock data");
        setPlans(SUBSCRIPTION_PLANS);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log("Fetching plans from:", ENDPOINTS.plans);
        console.log("User token present:", !!user?.token);
        console.log("Full auth header:", `Bearer ${user?.token}`);

        const response = await fetch(ENDPOINTS.plans, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        });

        console.log("Response status:", response.status);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error("API error response:", errorData);
          const errorMsg =
            errorData?.message ||
            errorData?.error ||
            `API Error: ${response.status} ${response.statusText}`;
          throw new Error(errorMsg);
        }

        const data = await response.json();
        console.log("Plans fetched successfully:", data);
        setPlans(Array.isArray(data) ? data : data.plans || []);
      } catch (err) {
        console.error("Error fetching plans:", err);
        console.log("Falling back to mock data...");
        setPlans(SUBSCRIPTION_PLANS);
        setError(null); // Clear error if using fallback
      } finally {
        setLoading(false);
      }
    };

    if (user?.token) {
      fetchPlans();
    }
  }, [user?.token]);

  // Handle plan selection and payment initiation
  const handlePlanSelect = async (plan) => {
    if (!user?.token || !user?.id) {
      alert("Please log in to continue");
      return;
    }

    try {
      setProcessingPayment(true);

      console.log("Initiating payment for plan:", plan);
      console.log("User ID:", user.id);

      const payload = {
        planId: String(plan.id),
        profilerId: String(user.id),
        durationNumber: plan.duration || 1, // Default to 1 month if not specified
        callbackUrl: `${window.location.origin}/payment/callback`,
      };

      console.log("Payment payload:", payload);

      const response = await fetch(ENDPOINTS.makePlanPayment, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      console.log("Payment initiation response status:", response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Payment initiation error response:", errorData);
        throw new Error(errorData.message || `Payment initiation failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log("Payment initiation success:", data);

      if (data.paymentUrl) {
        // Redirect to payment gateway
        window.location.href = data.paymentUrl;
      } else {
        throw new Error("No payment URL received");
      }
    } catch (err) {
      console.error("Payment initiation error:", err);
      alert(`Payment initiation failed: ${err.message}`);
    } finally {
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-gray-200 dark:border-gray-800 border-t-violet-500 rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading plans...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="bg-yellow-500/10 border border-yellow-500/25 rounded-xl p-4 mb-4">
            <p className="text-yellow-600 dark:text-yellow-400 font-semibold mb-2">
              Plan list unavailable
            </p>
            <p className="text-sm text-yellow-600 dark:text-yellow-400 mb-3">
              {error}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Using sample plans below. Plans from the database will load once the API is configured.
            </p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="bg-violet-600 hover:bg-violet-500 text-white px-4 py-2 rounded-xl text-sm font-semibold"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <div className="space-y-8 max-w-5xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center py-4">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-violet-600 uppercase tracking-widest bg-violet-50 border border-violet-200 px-3 py-1 rounded-full mb-4">
            <Sparkles size={12} /> Subscription Plans
          </span>
          <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mt-2">
            Choose your <span className="text-violet-600">growth plan</span>
          </h2>
          <p className="text-sm text-gray-600 mt-3 max-w-md mx-auto leading-relaxed">
            Unlock advanced features to grow your Lagos marketplace presence.
            Cancel anytime.
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-4">
          {plans.map((plan) => (
            <PricingCard
              key={plan.id}
              plan={plan}
              isCurrent={user.plan === plan.name}
              onSelect={() => handlePlanSelect(plan)}
              processing={processingPayment}
            />
          ))}
        </div>

        {/* Feature comparison note */}
        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">
            Plan Comparison
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="text-left py-2.5 pr-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Feature
                  </th>
                  {plans.map((p) => (
                    <th
                      key={p.id}
                      className="py-2.5 px-3 text-xs font-semibold text-gray-700 text-center whitespace-nowrap">
                      {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[
                  { label: "Product Listings", values: ["5", "50", "Unlimited"] },
                  { label: "Market Categories", values: ["1", "All", "All"] },
                  { label: "Featured Listings", values: ["✗", "5", "Unlimited"] },
                  {
                    label: "Analytics",
                    values: ["Basic", "Advanced", "Full Suite"],
                  },
                  {
                    label: "Support",
                    values: ["Email", "Priority", "Dedicated"],
                  },
                  { label: "Custom Storefront", values: ["✗", "✓", "✓"] },
                  { label: "Bulk Upload", values: ["✗", "✗", "✓"] },
                  { label: "API Access", values: ["✗", "✗", "✓"] },
                ].map((row) => (
                  <tr
                    key={row.label}
                    className="hover:bg-gray-100 transition-colors">
                    <td className="py-2.5 pr-4 text-gray-700 text-xs">
                      {row.label}
                    </td>
                    {row.values.map((v, i) => (
                      <td key={i} className="py-2.5 px-3 text-center">
                        <span
                          className={`text-xs font-medium ${
                            v === "✗"
                              ? "text-gray-400"
                              : v === "✓"
                                ? "text-emerald-600"
                                : "text-gray-800"
                          }`}>
                          {v}
                        </span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ snippet */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            {
              q: "Can I cancel anytime?",
              a: "Yes, you can cancel or downgrade your plan at any time. No lock-in periods.",
            },
            {
              q: "How does billing work?",
              a: "Billing is monthly in Nigerian Naira (₦). Payments are processed securely.",
            },
            {
              q: "What payment methods are accepted?",
              a: "We accept bank transfers, Paystack, Flutterwave, and major Nigerian payment methods.",
            },
            {
              q: "Is there a trial period?",
              a: "New merchants get a 14-day free trial of the Advanced plan upon registration.",
            },
          ].map(({ q, a }) => (
            <div key={q} className="card p-4">
              <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">
                {q}
              </h4>
              <p className="text-xs text-gray-500 leading-relaxed">{a}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
